import type { CombinedError } from '@urql/core';

import type { ConnectionView } from './types';

// What a Connect can end with, why a service cannot be connected, and how a `connectIntent` can
// refuse, with the copy for each (namespace `integrations`). ity-platform-backend
// `integrations.results.ts::CONNECT_RESULTS`, `integrations.status.ts::attemptOutcome`,
// `integrationConnect.service.ts::connectIntent`; the AI CIO card's reasons are CC D12's.

/** The complete page's results: connectors' own, then the CRM's. */
export const CONNECT_RESULTS = [
  'ok',
  'identity_mismatch',
  'partial_grant',
  'second_mail_provider',
  'expired',
  'admin_consent_needed',
  'interaction_required',
  'blocked_by_admin',
  'guest_not_supported',
  'wrong_site',
  'wrong_account',
  'error',
  'link_conflict',
  'not_entitled',
  'disconnect_pending',
  'try_again',
  'denied',
] as const;

/**
 * Results (and the reason `cancelled`) that leave a stopped Connect rather than an error, as the
 * CRM reads them (`integrations.status.ts::attemptOutcome`).
 */
export const STOPPED_RESULTS: readonly string[] = [
  'expired',
  'denied',
  'cancelled',
  'not_entitled',
  'disconnect_pending',
];

/** Results the Connect card shows as Denied (CC D15); every other result is an Error. */
export const DENIED_RESULTS: readonly string[] = ['denied', 'expired', 'cancelled'];

/** Why a service cannot be connected here (CC D12), `cannot_connect_now` for any other reason. */
export const UNAVAILABLE_REASONS = [
  'not_offered',
  'no_permission',
  'firm_setup_needed',
  'not_entitled',
  'other_mail_connected',
  'disconnect_pending',
  'blocked_by_admin',
  'cannot_connect_now',
] as const;

/** Reasons that make an entitled service not offerable (CC D4). */
export const UNOFFERABLE_REASONS: readonly string[] = [
  'other_mail_connected',
  'disconnect_pending',
  'blocked_by_admin',
];

/**
 * `connectIntent`'s refusals (`integrationConnect.service.ts::connectIntent`; GS adds
 * `OTHER_MAIL_CONNECTED`). The CRM masks a ForbiddenException's message as "Access denied", so
 * `INTEGRATIONS_CALLER_REFUSED` and `NOT_ENTITLED` both arrive as `FORBIDDEN` and read as
 * `NOT_ENTITLED` (a caller the CRM refuses never reaches AI CIO); a 503 reads as
 * `CONNECTORS_UNAVAILABLE`, the one 503 `connectIntent` throws.
 */
export const CONNECT_INTENT_ERRORS = [
  'INTEGRATIONS_CALLER_REFUSED',
  'INTEGRATIONS_CHANNELS_TEAMS_ONLY',
  'CONNECTORS_UNAVAILABLE',
  'INTEGRATIONS_BROWSER_NOT_BOUND',
  'NOT_ENTITLED',
  'DISCONNECT_PENDING',
  'OTHER_MAIL_CONNECTED',
] as const;

/** A key in the `integrations` namespace and its English text. */
export interface Copy {
  key: string;
  defaultValue: string;
}

/**
 * A table's own entry. Codes and services come from a URL and pass by shape alone, so one named
 * like an `Object.prototype` member (`constructor`, `__proto__`) must read as absent.
 */
function own<T>(table: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

const RESULT_TEXT: Record<string, string> = {
  ok: 'Connected.',
  identity_mismatch:
    'The account you signed in with is not the one connected here. Sign in with the same account.',
  partial_grant: 'Not every permission was granted. Try again and allow all of them.',
  second_mail_provider: 'Another mail account is already connected. Only one can be.',
  expired: 'The sign-in took too long or its link was already used. Try again.',
  admin_consent_needed: "Your firm's administrator must approve Investbanq first.",
  interaction_required: 'The provider needs you to sign in again. Try again.',
  blocked_by_admin: "Your firm's administrator has blocked this app.",
  guest_not_supported: "Guest accounts can't be connected. Sign in with your own work account.",
  wrong_site: "That site isn't your firm's. Sign in to your firm's site.",
  wrong_account: "That account isn't your firm's. Sign in with your firm's account.",
  error: 'Something went wrong. Try again.',
  link_conflict: 'This account is already connected to another user here.',
  not_entitled: 'Connected accounts are not available to you in this firm.',
  disconnect_pending: 'It is being disconnected; try again in a minute.',
  try_again: "That didn't work. Try again.",
  denied: 'Access was declined.',
  cancelled: 'The sign-in was cancelled.',
  unknown: 'Something went wrong. Try again.',
};

/**
 * Result copy that differs by provider (CC D15, GS §9): Google's `wrong_account` is a personal
 * account where a Workspace one is needed; DocuSign's and Dropbox's are their account and team pins;
 * Slack's is its team pin (or the Enterprise Grid org's); monday.com's is its account pin, or a
 * guest, a viewer, or a user who is not active or has not confirmed their email.
 */
const RESULT_TEXT_BY_PROVIDER: Record<string, Record<string, string>> = {
  google: {
    wrong_account:
      "Sign in with your company's Google Workspace account; personal Gmail accounts can't be connected.",
  },
  docusign: {
    wrong_account: "That DocuSign account isn't your firm's. Sign in with your firm's account.",
  },
  dropbox: {
    wrong_account:
      "That Dropbox account isn't in your firm's team. Sign in with your team account; personal accounts can't be connected.",
  },
  slack: {
    wrong_account: "That Slack workspace isn't your firm's. Sign in to your firm's workspace.",
  },
  monday: {
    wrong_account:
      "Sign in as an active member of your firm's monday.com account with a confirmed email; guests and viewers can't be connected.",
  },
};

/**
 * Each service's provider, for result copy where no catalog read is at hand: the complete page
 * runs outside the app's session. The CRM catalog's `connector.provider`, lowercase.
 */
const SERVICE_PROVIDER: Readonly<Record<string, string>> = {
  outlook: 'microsoft',
  teams: 'microsoft',
  onedrive: 'microsoft',
  confluence: 'atlassian',
  // Jira's own Atlassian app, its own provider (DEV-4570).
  jira: 'jira',
  docusign: 'docusign',
  dropbox: 'dropbox',
  gmail: 'google',
  google_calendar: 'google',
  google_drive: 'google',
  slack: 'slack',
  monday: 'monday',
};

/** A service's provider, lowercase, as `SERVICE_PROVIDER` maps it; undefined for any other. */
export function serviceProvider(service: string): string | undefined {
  return own(SERVICE_PROVIDER, service);
}

const REASON_TEXT: Record<(typeof UNAVAILABLE_REASONS)[number], string> = {
  not_offered: 'Your firm does not offer it.',
  no_permission: 'Your role does not include it.',
  firm_setup_needed: 'An administrator has not set it up for your firm.',
  not_entitled: 'Connected accounts are not available to you in this firm.',
  other_mail_connected: 'Your other mail account is already set up here, and only one can be.',
  disconnect_pending: 'It is being disconnected; try again in a minute.',
  blocked_by_admin: "Your firm's administrator has blocked it; contact them.",
  cannot_connect_now: 'It cannot be connected right now.',
};

const INTENT_ERROR_TEXT: Record<string, string> = {
  INTEGRATIONS_CALLER_REFUSED: "You can't connect accounts in this session.",
  INTEGRATIONS_CHANNELS_TEAMS_ONLY: 'Channel reading is only for Microsoft Teams.',
  CONNECTORS_UNAVAILABLE: 'Connected accounts are unavailable right now. Try again later.',
  INTEGRATIONS_BROWSER_NOT_BOUND:
    "This browser couldn't be prepared for sign-in. Reload the page and try again.",
  NOT_ENTITLED: 'Connected accounts are not available to you in this firm.',
  DISCONNECT_PENDING: 'It is being disconnected; try again in a minute.',
  OTHER_MAIL_CONNECTED: 'Another mail account is already set up here, and only one can be.',
  UNKNOWN: "The sign-in couldn't start. Try again.",
};

export function isConnectResult(value: string): boolean {
  return (CONNECT_RESULTS as readonly string[]).includes(value);
}

/** The copy for a Connect's result, by provider where it differs; an unknown code reads as an error. */
export function resultCopy(result: string, provider?: string): Copy {
  const providerText = provider ? own(RESULT_TEXT_BY_PROVIDER, provider.toLowerCase()) : undefined;
  const byProvider = providerText ? own(providerText, result) : undefined;
  if (provider && byProvider) {
    return {
      key: `resultByProvider.${provider.toLowerCase()}.${result}`,
      defaultValue: byProvider,
    };
  }
  const text = own(RESULT_TEXT, result);
  return text ? { key: `result.${result}`, defaultValue: text } : resultCopy('unknown');
}

/** The reason a service cannot be connected, as CC D12 names it. */
export function unavailableReason(reason: string | null | undefined): string {
  return reason && reason !== 'cannot_connect_now' && own(REASON_TEXT, reason) !== undefined
    ? reason
    : 'cannot_connect_now';
}

export function reasonCopy(reason: string | null | undefined): Copy {
  const code = unavailableReason(reason) as keyof typeof REASON_TEXT;
  return { key: `reason.${code}`, defaultValue: REASON_TEXT[code] };
}

/** The copy for a `connectIntent` refusal; a code not listed reads as `UNKNOWN`. */
export function intentErrorCopy(code: string): Copy {
  const known = own(INTENT_ERROR_TEXT, code) !== undefined ? code : 'UNKNOWN';
  return { key: `intentError.${known}`, defaultValue: INTENT_ERROR_TEXT[known] ?? '' };
}

export function isIntentError(code: string): boolean {
  return (CONNECT_INTENT_ERRORS as readonly string[]).includes(code);
}

/**
 * The code of a failed `connectIntent`, read from the CRM's unified GraphQL error: one of
 * CONNECT_INTENT_ERRORS, else `UNKNOWN` (a network failure included). Upper case, as no result
 * code is.
 */
export function readIntentError(error: CombinedError): string {
  const first = error.graphQLErrors[0];
  if (!first) return 'UNKNOWN';
  const ext = first.extensions;
  for (const candidate of [first.message, ext.error_message, ext.error]) {
    if (typeof candidate === 'string' && isIntentError(candidate)) return candidate;
  }
  if (ext.code === 'FORBIDDEN') return 'NOT_ENTITLED';
  if (ext.error === 'SERVICE_UNAVAILABLE' || ext.statusCode === 503)
    return 'CONNECTORS_UNAVAILABLE';
  return 'UNKNOWN';
}

/** The service may be offered a Connect (CC D4): entitled, and not held back by its reason. */
export function isOfferable(view: ConnectionView): boolean {
  return view.entitled && !UNOFFERABLE_REASONS.includes(view.reason ?? '');
}
